import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    likedMixes: [],
    likedMixSet: {}, // "title|||djName" -> true for quick lookup
    total: 0,
    page: 1,
    loading: false,
    sidebarOpen: false,
    error: null,
};

// the DJ words of a billing (lowercase, accents off, joiners dropped)
const djWords = (s) => new Set(String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .split(/[^a-z0-9]+/).filter(w => w.length >= 2 && !['and', 'b2b', 'vs', 'feat', 'ft', 'dj', 'the'].includes(w)));
// the resolved row for a liked snapshot among the rows sharing its link
export function pickLinkMatch(rows, dj) {
    if (!rows || !rows.length) return null;
    if (rows.length === 1) return rows[0];
    const mine = djWords(dj);
    const hits = rows.filter(e => [...djWords(e.djName || e.artist)].some(w => mine.has(w)));
    return hits.length === 1 ? hits[0] : null;
}

const likedMixesSlice = createSlice({
    name: 'likedMixes',
    initialState,
    reducers: {
        // Dispatched on logout: liked mixes are user-scoped and must not
        // linger for the next account on this device.
        clearLikedMixes(state) {
            Object.assign(state, initialState);
        },
        setLikedMixes(state, action) {
            state.likedMixes = action.payload.data;
            state.total = action.payload.total;
        },
        appendLikedMixes(state, action) {
            state.likedMixes = [...state.likedMixes, ...action.payload.data];
            state.total = action.payload.total;
        },
        // Merge server-resolved metadata (by link) into liked-mix snapshots:
        // `id` (the catalog public id → open/play by id, fast),
        // cover_from_track (→ native cover), and artists[] (→ per-DJ
        // clickable segments instead of one merged link). Rows are already
        // boundary-normalized (likeId holds the like-row id), so `id` here is
        // ONLY ever the catalog id.
        //
        // One link can resolve to SEVERAL mixes: a multi-set page (a radio show
        // with a guest mix) is one mix per set and the sets share the show's
        // recording link. Taking the last row per link opened a liked guest mix
        // as its host. So a link with several rows picks the one whose DJs match
        // the liked snapshot's DJ, and leaves the snapshot alone when none or
        // several do.
        enrichLikedMixes(state, action) {
            const byLink = new Map();
            for (const e of (action.payload || [])) {
                if (!e || !e.link) continue;
                if (!byLink.has(e.link)) byLink.set(e.link, []);
                byLink.get(e.link).push(e);
            }
            if (!byLink.size) return;
            state.likedMixes = state.likedMixes.map(m => {
                const e = m.link && pickLinkMatch(byLink.get(m.link), m.dj_name || m.djName);
                if (!e) return m;
                return {
                    ...m,
                    id: e.id ?? m.id,
                    cover_from_track: e.coverFromTrack ?? m.cover_from_track,
                    artists: (Array.isArray(e.artists) && e.artists.length) ? e.artists : m.artists,
                };
            });
        },
        addMixLike(state, action) {
            const mix = action.payload;
            state.likedMixes = [mix, ...state.likedMixes];
            state.total += 1;
            state.likedMixSet[`${mix.title}|||${mix.dj_name || mix.djName}`] = true;
        },
        removeMixLike(state, action) {
            const { title, djName } = action.payload;
            state.likedMixes = state.likedMixes.filter(
                m => !(m.title === title && (m.dj_name === djName || m.djName === djName))
            );
            state.total = Math.max(0, state.total - 1);
            delete state.likedMixSet[`${title}|||${djName}`];
        },
        setLikedMixSet(state, action) {
            state.likedMixSet = action.payload;
        },
        updateLikedMixSet(state, action) {
            Object.assign(state.likedMixSet, action.payload);
        },
        setMixPage(state, action) {
            state.page = action.payload;
        },
        setMixLoading(state, action) {
            state.loading = action.payload;
        },
        setLikedMixesSidebarOpen(state, action) {
            state.sidebarOpen = action.payload;
        },
        toggleLikedMixesSidebar(state) {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setMixError(state, action) {
            state.error = action.payload;
        },
    },
});

export const {
    clearLikedMixes,
    setLikedMixes,
    appendLikedMixes,
    enrichLikedMixes,
    addMixLike,
    removeMixLike,
    setLikedMixSet,
    updateLikedMixSet,
    setMixPage,
    setMixLoading,
    setLikedMixesSidebarOpen,
    toggleLikedMixesSidebar,
    setMixError,
} = likedMixesSlice.actions;

export default likedMixesSlice.reducer;
